import type { ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export type BannerTone = "info" | "success" | "warning" | "danger" | "restriction";

const icons: Record<BannerTone, IconName> = {
  info: "info", success: "check-circle", warning: "warning", danger: "warning", restriction: "restriction",
};

export interface BannerProps {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Buttons, e.g. the mandatory "I have checked" acknowledgement for a restriction. */
  actions?: ReactNode;
  className?: string;
}

/**
 * An inline message. "restriction" is black on amber with a heavy edge, an icon and the word
 * RESTRICTION, in every presentation and theme: never red, so it stays legible in sunlight.
 * Restriction and danger banners are announced assertively (role="alert").
 */
export function Banner({ tone = "info", title, children, actions, className }: BannerProps) {
  const urgent = tone === "restriction" || tone === "danger";
  return (
    <div className={cx("pmu-banner", `pmu-banner--${tone}`, className)} role={urgent ? "alert" : "status"}>
      <Icon name={icons[tone]} className="pmu-banner__icon" />
      <div className="pmu-banner__body">
        {tone === "restriction" && <strong className="pmu-banner__kicker">RESTRICTION</strong>}
        {title && <div className="pmu-banner__title">{title}</div>}
        {children && <div className="pmu-banner__text">{children}</div>}
      </div>
      {actions && <div className="pmu-banner__actions">{actions}</div>}
    </div>
  );
}
